import { useDateField } from '@atlassian/jira-issue-field-date';
import {
	START_DATE_TYPE,
	UPDATED,
	CREATED,
	DUE_DATE_TYPE,
	DATE_CF_TYPE,
} from '@atlassian/jira-platform-field-config';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasPlainTextFormat,
	useClipboardHandler,
} from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	ListIssueForCopyPaste,
	TransformedDateField,
	PasteHandler,
	PasteMetadata,
} from '../../../types';
import { createClipboardHelperComponent } from '../../utils';
import { dateSupportedTypes } from '../supported-field-types';

const getPastedValue = (clipboardItem: ClipboardItem, metadata: PasteMetadata | null) => {
	if (
		!assertClipboardHasPlainTextFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, dateSupportedTypes)
	) {
		return null;
	}

	return clipboardItem.plainText;
};

function assertDateField(field: ListIssueFieldTransformed): asserts field is TransformedDateField {
	if (!field) {
		throw new Error('Field is required');
	}

	if (
		field.type !== START_DATE_TYPE &&
		field.type !== UPDATED &&
		field.type !== CREATED &&
		field.type !== DUE_DATE_TYPE &&
		field.type !== DATE_CF_TYPE
	) {
		throw new Error('Field type is not supported');
	}
}

const useDateClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	assertDateField(field);

	const [{ value }, { saveValue }] = useDateField({
		issueKey: issue.key,
		fieldKey: field?.fieldId,

		initialValue: field?.value,
	});

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: value,
		currentValue: value,
		getPastedValue,
		saveValue,
	});
};

const DateClipboardHelper = createClipboardHelperComponent(useDateClipboardHandler);

export const dateClipboardHandler = {
	[START_DATE_TYPE]: DateClipboardHelper,
	[UPDATED]: DateClipboardHelper,
	[CREATED]: DateClipboardHelper,
	[DUE_DATE_TYPE]: DateClipboardHelper,
	[DATE_CF_TYPE]: DateClipboardHelper,
};
